import moment from "moment";
import * as XLSX from "xlsx";
import { PharmaStpFileApiRow } from "../api/bigQuery/pharmaStpFileOrders";
import { AuthStatusChange } from "../components/modals/StatusUpdateModal/StatusUpdateModal";
import { AuthStatusOptionModel } from "../data-model/authStatusOptions";
import { FinalWorklistDataResponse } from "../data-model/finalWorklistModel";
import { MedicalPaOrder } from "../data-model/medicalPaOrdersModel";
import { OncologySubmissionTableDataModel } from "../data-model/tablesData";
import { generateFullName } from "./generateOrderDataForTable";
import { getAuthStatusTextOfMedicalPaOrder } from "./getAuthStatusOfMedicalPaOrder";
import { getFinalStatusOfOrder } from "./getFinalStatusOfOrder";
import { capitalizeString } from "./stringModifications";

export const exportDataToExcel = (data: any, filename: string) => {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(data);

  // Set column widths for better visibility
  const columnWidths = [
    { wch: 25 }, // order_id
    { wch: 25 }, // Patient Name
    { wch: 15 }, // Patient MRN
    { wch: 15 }, // Date of Service
    { wch: 40 }, // Medication
    { wch: 25 }, // Form Name
    { wch: 20 }, // CMM Key
    { wch: 15 }, // Form Diff
    { wch: 40 }, // Form Diff Details
    { wch: 15 }, // QA Diff
    { wch: 15 }, // Status
    { wch: 15 }, // Location
    { wch: 50 }, // Comment
    { wch: 15 }, // Record ID
  ];

  worksheet["!cols"] = columnWidths;

  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  const safeFilename = filename.endsWith(".xlsx")
    ? filename
    : `${filename}.xlsx`;
  XLSX.writeFile(workbook, safeFilename);
};

export const exportDataFromTableForMedicalPaOrders = (
  data: MedicalPaOrder[],
) => {
  if (data.length === 0) {
    return [];
  }
  return data.map((item) => {
    return {
      "Order ID": item?.id ?? "",
      "Created At": moment(item?.created_at).format("DD/MMM/YYYY") ?? "",
      "Patient Name": capitalizeString(
        generateFullName(
          item?.demographics?.first_name ?? "",
          "",
          item?.demographics?.last_name ?? "",
        ),
      ),
      "Patient DOB": item?.demographics?.date_of_birth ?? "",
      MRN: item?.demographics?.patient_mrn ?? "",
      "Plan Name (Primary)": item?.coverage?.primary?.payer_name ?? "",
      "Member ID (Primary)": item?.coverage?.primary?.member_id ?? "",
      "Plan Name (Secondary)": item?.coverage?.secondary?.payer_name ?? "",
      "Member ID (Secondary)": item?.coverage?.secondary?.member_id ?? "",
      Medication: item?.payload?.regimen_name ?? "",
      "Date Of Service":
        moment(item?.payload?.date_of_service).format("MM/DD/YYYY") ?? "",
      "Provider Name": capitalizeString(item?.payload?.practitioner_name ?? ""),
      "Auth Issue Comments":
        getAllComments(item)?.["Auth Issue Comments"] ?? "",
      "Auth Issue Reason": getAllReason(item)?.["Auth Issue Reason"] ?? "",
      "BO Status": item?.status?.bo_status ?? "",
      "Auth Verification": item?.auth_on_file?.auth_status ?? "",
      Location: item?.payload?.location ?? "",
      Assignee: item?.assigned_to?.provider_name ?? "",
      "Denial Comments": getAllComments(item)?.["Denial Comments"] ?? "",
      "Denial Reason": getAllReason(item)?.["Denial Reason"] ?? "",
    };
  });
};
// logic for cmm diff data
export const exportDataFromTableForCmmDiffData = (tableData: any[]) => {
  if (tableData.length === 0) {
    return [];
  }
  return tableData.map((item) => {
    return {
      "Patient Name": item.pharmaPaDiffPatientDetails?.mainText ?? "--",
      "Patient MRN":
        item.pharmaPaDiffPatientDetails?.secondaryText?.replace("MRN : ", "") ??
        "--",
      "Date of Service": item.pharmaPaDiffDateOfService ?? "--",
      Medication: item.pharmaPaDiffMedication ?? "--",
      "Form Name": item.pharmaPaDiffFormName ?? "--",
      "CMM Key": item.pharmaPaDiffCmmKey ?? "--",
      "Form Diff": item.pharmaPaDiffFormDiffFields?.text ?? "--",
      "Form Diff Details": item.pharmaPaDiffFormDiffFields?.displayText ?? "--",
      "QA Diff": item.pharmaPaDiffQaDiffFields?.text ?? "--",
      Status: item.pharmaPaDiffStatus?.text ?? "--",
      Comment: item.pharmaPaDiffCommentString ?? "--",
      "Record ID": item.id ?? "--",
    };
  });
};

export const exportDataFromTableForExternalPaOrders = (
  data: FinalWorklistDataResponse[],
  authStatusOptions: AuthStatusOptionModel[],
) => {
  if (data.length === 0) {
    return [];
  }
  return data.map((item) => {
    return {
      "Patient Name": capitalizeString(
        generateFullName(item?.patient_name ?? "", "", ""),
      ),
      "Patient MRN": item?.mrn ?? "",
      "Date of Service":
        moment(item?.date_of_service).format("MM/DD/YYYY") ?? "",
      "Date of Work (DoW)":
        moment(item?.date_of_work).format("MM/DD/YYYY") ?? "",
      "Plan Name (Primary)": item?.primary_insurance ?? "",
      "Plan Name (Secondary)": item?.secondary_ins ?? "",
      "Date of Birth (DoB)": moment(item?.dob).format("MM/DD/YYYY") ?? "",
      "ID (Insurance ID)": item?.id ?? "",
      Medication: item?.regimen ?? "",
      Location: item?.location ?? "",
      "Provider Name": capitalizeString(item?.md ?? ""),
      "Created At": moment(item?.date_of_work).format("MM/DD/YYYY") ?? "",
      "Auth Verification": item?.auth_status ?? "",
      "BO status": getFinalStatusOfOrder(item?.bo_value).text ?? "",
      "Auth Issue Comments":
        getAllComments(item)?.["Auth Issue Comments"] ?? "",
      "Denial Comments": getAllComments(item)?.["Denial Comments"] ?? "",
    };
  });
};

export const exportDataFromTableForExternalPaOrdersNew = (
  data: MedicalPaOrder[],
  authStatusOptions: AuthStatusOptionModel[],
) => {
  if (data.length === 0) {
    return [];
  }
  return data.map((item) => {
    return {
      "Date Of Work": moment(item?.created_at).format("MM/DD/YYYY") ?? "",
      "Patient MRN": item?.demographics?.patient_mrn ?? "",
      "Patient Name": capitalizeString(
        generateFullName(
          item?.demographics?.first_name ?? "",
          "",
          item?.demographics?.last_name ?? "",
        ),
      ),
      "Date Of Birth": item?.demographics?.date_of_birth ?? "",
      MD: item?.payload?.practitioner_name ?? "",
      "Plan Name (Primary)": item?.coverage?.primary?.payer_name ?? "",
      "Member ID (Primary)": item?.coverage?.primary?.member_id ?? "",
      "Plan Name (Secondary)": item?.coverage?.secondary?.payer_name ?? "",
      "Member ID (Secondary)": item?.coverage?.secondary?.member_id ?? "",
      Medication: item?.payload?.regimen_name ?? "",
      "Date Of Service":
        moment(item?.payload?.date_of_service).format("lll") ?? "",
      "Auth Status":
        getAuthStatusTextOfMedicalPaOrder(item, authStatusOptions ?? []) ?? "",
      "BO Status": item?.status?.bo_status ?? "",
      Location: item?.payload?.location ?? "",
      "Auth Issue Comments":
        getAllComments(item)?.["Auth Issue Comments"] ?? "",
      "Denial Comments": getAllComments(item)?.["Denial Comments"] ?? "",
    };
  });
};

export const getAllComments = (item: MedicalPaOrder) => {
  const auth_status_changes = item?.auth_status_changes || [];
  const queryComments = auth_status_changes?.filter(
    (item: AuthStatusChange) => item.new_value === "query",
  );
  const denialComments = auth_status_changes?.filter(
    (item: AuthStatusChange) => item.new_value === "denied_by_risa",
  );
  const queryCommentsText = queryComments?.map(
    (item: AuthStatusChange) =>
      "created at: " +
      moment(item.updated_at).format("MM/DD/YYYY") +
      " " +
      "Query Comment : " +
      item.comment +
      " " +
      ";",
  );
  const denialCommentsText = denialComments?.map(
    (item: AuthStatusChange) =>
      "created at: " +
      moment(item.updated_at).format("MM/DD/YYYY") +
      " " +
      "Denial Comment: " +
      item.comment +
      " " +
      ";",
  );

  return {
    "Auth Issue Comments": queryCommentsText?.join(" "),
    "Denial Comments": denialCommentsText?.join(" "),
  };
};

export const getAllReason = (item: MedicalPaOrder) => {
  const auth_status_changes = item?.auth_status_changes || [];
  const queryComments = auth_status_changes?.filter(
    (item: AuthStatusChange) => item.new_value === "query",
  );
  const denialComments = auth_status_changes?.filter(
    (item: AuthStatusChange) => item.new_value === "denied_by_risa",
  );
  const queryCommentsText = queryComments?.map(
    (item: AuthStatusChange) => item.reason,
  );
  const denialCommentsText = denialComments?.map(
    (item: AuthStatusChange) => item.reason,
  );

  return {
    "Auth Issue Reason": queryCommentsText?.join(" ; "),
    "Denial Reason": denialCommentsText?.join(" ; "),
  };
};

/**
 * Parses a patient name string and extracts last name and first name
 * Handles formats like "Last, First", "First Last", or single names
 * @param patientName - The patient name string to parse
 * @returns Object containing lastName and firstName
 */
const parsePatientName = (
  patientName: string,
): { lastName: string; firstName: string } => {
  if (!patientName || patientName.trim() === "") {
    return { lastName: "", firstName: "" };
  }

  const trimmedName = patientName.trim().toLowerCase();

  // Check if name is in "Last, First" format
  if (trimmedName.includes(",")) {
    const parts = trimmedName.split(",").map((part) => part.trim());
    return {
      lastName: parts[0] || "",
      firstName: parts[1] || "",
    };
  }

  // Assume name is in "First Last" format
  const parts = trimmedName.split(" ").filter((part) => part.length > 0);
  if (parts.length === 1) {
    return { lastName: parts[0], firstName: "" };
  }

  // Last part is last name, everything else is first name
  const lastName = parts[parts.length - 1];
  const firstName = parts.slice(0, -1).join(" ");

  return { lastName, firstName };
};

/**
 * Sorts Pharma STP File data alphabetically by last name, then first name
 * @param data - Array of PharmaStpFileApiRow from the API response
 * @returns Sorted array of PharmaStpFileApiRow
 */
export const sortPharmaStpFileDataByName = (
  data: PharmaStpFileApiRow[],
): PharmaStpFileApiRow[] => {
  return [...data].sort((a, b) => {
    const nameA = parsePatientName(a.patient_name || "");
    const nameB = parsePatientName(b.patient_name || "");

    // Compare by last name first
    const lastNameComparison = nameA.lastName.localeCompare(nameB.lastName);
    if (lastNameComparison !== 0) {
      return lastNameComparison;
    }

    // If last names are equal, compare by first name
    return nameA.firstName.localeCompare(nameB.firstName);
  });
};

/**
 * Transform Pharma STP File data for Excel export
 * @param data - Array of PharmaStpFileApiRow from the API response
 * @returns Transformed data array ready for Excel export
 */
export const transformPharmaStpFileDataForExport = (
  data: PharmaStpFileApiRow[],
) => {
  if (data.length === 0) {
    return [];
  }
  return data.map((item) => {
    return {
      Identifier: item?.identifier ?? "--",
      Seq: item?.seq ?? "--",
      POC: item?.poc ?? "--",
      "Pharmacy Type": item?.pharmacy_type ?? "--",
      "Patient MRN": item?.patient_mrn ?? "--",
      "Patient Name": capitalizeString(item?.patient_name ?? "") || "--",
      DOB: item?.dob ?? "--",
      "Insurance ID": item?.insuranceid ?? "--",
      "Provider Name": capitalizeString(item?.provider_name ?? "") || "--",
      Drug: item?.drug ?? "--",
      Pharmacy: item?.pharmacy ?? "--",
      BIN: item?.bin ?? "--",
      "Pharmacy Phone": item?.pharmacy_phone ?? "--",
      "RX Due Date": item?.rx_due_date ?? "--",
      "CoverMyMeds ID": item?.covermymed_id ?? "--",
      "Response Status": item?.response_status ?? "--",
      "Second STP Status": item?.second_stp_status ?? "--",
      "SFTP Status": item?.sftp_status ?? "--",
      Filename: item?.filename ?? "--",
      "Org ID": item?.org_id ?? "--",
      "Dumped At": item?.dumped_at
        ? moment(item.dumped_at).format("MM/DD/YYYY HH:mm")
        : "--",
      "Text Note Status": item?.text_note_status ?? "--",
      "Route Via": item?.route_via ?? "--",
      "PA Status Reason": item?.pa_status_reason ?? "--",
      "CMM ST RPA Status": item?.meta?.cmm_st_rpa_status ?? "--",
    };
  });
};

/**
 * Export Pharma STP File data to Excel
 * Records are sorted alphabetically by last name, first name before export
 * @param data - Array of PharmaStpFileApiRow from the API response
 * @param filename - Optional filename for the Excel file (defaults to timestamped name)
 */
export const exportPharmaStpFileDataToExcel = (
  data: PharmaStpFileApiRow[],
  filename?: string,
) => {
  // Sort data alphabetically by last name, first name
  const sortedData = sortPharmaStpFileDataByName(data);
  const transformedData = transformPharmaStpFileDataForExport(sortedData);

  if (transformedData.length === 0) {
    console.warn("No data to export");
    return;
  }

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(transformedData);

  // Set column widths for better visibility
  const columnWidths = [
    { wch: 20 }, // Identifier
    { wch: 10 }, // Seq
    { wch: 15 }, // POC
    { wch: 15 }, // Pharmacy Type
    { wch: 15 }, // Patient MRN
    { wch: 25 }, // Patient Name
    { wch: 12 }, // DOB
    { wch: 20 }, // Insurance ID
    { wch: 25 }, // Provider Name
    { wch: 30 }, // Drug
    { wch: 20 }, // Pharmacy
    { wch: 12 }, // BIN
    { wch: 15 }, // Pharmacy Phone
    { wch: 15 }, // RX Due Date
    { wch: 20 }, // CoverMyMeds ID
    { wch: 18 }, // Response Status
    { wch: 18 }, // Second STP Status
    { wch: 15 }, // SFTP Status
    { wch: 30 }, // Filename
    { wch: 15 }, // Org ID
    { wch: 20 }, // Dumped At
    { wch: 18 }, // Text Note Status
    { wch: 15 }, // Route Via
    { wch: 20 }, // PA Status Reason
    { wch: 20 }, // CMM ST RPA Status
  ];

  worksheet["!cols"] = columnWidths;

  XLSX.utils.book_append_sheet(workbook, worksheet, "Pharma STP Data");

  const defaultFilename = `pharma_stp_export_${moment().format("YYYYMMDD_HHmmss")}.xlsx`;
  const safeFilename = filename
    ? filename.endsWith(".xlsx")
      ? filename
      : `${filename}.xlsx`
    : defaultFilename;

  XLSX.writeFile(workbook, safeFilename);
};

/**
 * Transform Oncology Submission data for Excel export
 * Format: Date, Patient Name, Drug, Key, PA Status, PA Status Reason, Comments
 * @param data - Array of OncologySubmissionTableDataModel from the table
 * @returns Transformed data array ready for Excel export
 */
export const transformOncologySubmissionDataForExport = (
  data: OncologySubmissionTableDataModel[],
) => {
  if (data.length === 0) {
    return [];
  }
  return data.map((item) => {
    return {
      Date: item?.dow ?? "--",
      "Patient Name":
        capitalizeString(item?.patientName?.mainText ?? "") || "--",
      Drug: item?.drug ?? "--",
      Key: item?.cmmId ?? "--",
      "PA Status": item?.responseStpStatus?.text ?? "--",
      "PA Status Reason": item?.paStatusReason ?? "--",
      Comments: item?.comment ?? "--",
    };
  });
};

/**
 * Sort Oncology Submission data alphabetically by patient last name, then first name
 * @param data - Array of OncologySubmissionTableDataModel
 * @returns Sorted array
 */
export const sortOncologySubmissionDataByName = (
  data: OncologySubmissionTableDataModel[],
): OncologySubmissionTableDataModel[] => {
  return [...data].sort((a, b) => {
    const nameA = parsePatientName(a.patientName?.mainText ?? "");
    const nameB = parsePatientName(b.patientName?.mainText ?? "");

    // Compare by last name first (case-insensitive)
    const lastNameComparison = nameA.lastName.localeCompare(
      nameB.lastName,
      undefined,
      {
        sensitivity: "base",
      },
    );
    if (lastNameComparison !== 0) {
      return lastNameComparison;
    }

    // If last names are equal, compare by first name (case-insensitive)
    return nameA.firstName.localeCompare(nameB.firstName, undefined, {
      sensitivity: "base",
    });
  });
};

/**
 * Export selected Oncology Submission data to Excel
 * Records are sorted alphabetically by last name, first name before export
 * @param data - Array of OncologySubmissionTableDataModel (selected rows)
 * @param filename - Optional filename for the Excel file (defaults to timestamped name)
 */
export const exportOncologySubmissionDataToExcel = (
  data: OncologySubmissionTableDataModel[],
  filename?: string,
) => {
  // Sort data alphabetically by last name, first name
  const sortedData = sortOncologySubmissionDataByName(data);
  const transformedData = transformOncologySubmissionDataForExport(sortedData);

  if (transformedData.length === 0) {
    console.warn("No data to export");
    return;
  }

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(transformedData);

  // Set column widths for better visibility matching the screenshot format
  const columnWidths = [
    { wch: 15 }, // Date
    { wch: 25 }, // Patient Name
    { wch: 20 }, // Drug
    { wch: 15 }, // Key
    { wch: 20 }, // PA Status
    { wch: 30 }, // PA Status Reason
    { wch: 50 }, // Comments
  ];

  worksheet["!cols"] = columnWidths;

  XLSX.utils.book_append_sheet(workbook, worksheet, "Oncology Submission");

  // Sanitize filename to prevent path traversal and invalid characters
  const sanitizeFilename = (fname: string): string => {
    return fname
      .replace(/[/\\:*?"<>|]/g, "_") // Remove invalid characters
      .replace(/^\.+/, "") // Remove leading dots
      .substring(0, 255); // Limit length
  };

  const defaultFilename = `oncology_submission_export_${moment().format("YYYYMMDD_HHmmss")}.xlsx`;
  let safeFilename = defaultFilename;

  if (filename) {
    const sanitized = sanitizeFilename(filename);
    safeFilename = sanitized.endsWith(".xlsx")
      ? sanitized
      : `${sanitized}.xlsx`;
  }

  XLSX.writeFile(workbook, safeFilename);
};
